import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const setEventEmailConfigMutation = gql`
  mutation setEventEmailConfig($input: SetEventEmailConfigInput!) {
    setEventEmailConfig(input: $input) {
      _id
      title
    }
  }
`;

export type SetEventEmailConfigInput = {
  eventId: string;
  confirmationEmailEnabled?: boolean;
  invitationEmailEnabled?: boolean;
};

type EventResult = {
  _id: string;
  title: string;
};

type ResultType = {
  setEventEmailConfig: EventResult;
};

type UseSetEventEmailConfigMutation = [
  (input: SetEventEmailConfigInput) => Promise<EventResult | null>,
  MutationResult<ResultType>,
];

export const useSetEventEmailConfigMutation = (): UseSetEventEmailConfigMutation => {
  const [setEventEmailConfig, state] = useMutation<ResultType>(setEventEmailConfigMutation);

  const executeMutation = useCallback(async (input: SetEventEmailConfigInput) => {
    try {
      const { data } = await setEventEmailConfig({
        variables: { input },
      });

      return data?.setEventEmailConfig || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, { eventId: input.eventId });
        Logger.logError({
          error,
          customContext,
          transactionName: 'setEventEmailConfig.ts - useSetEventEmailConfigMutation',
        });
      }
      return null;
    }
  }, [setEventEmailConfig]);

  return [executeMutation, state];
};
